import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  const navLinks = [
    ['works', 'Works'],
    ['manifesto', 'Manifesto'],
    ['about', 'About'],
    ['contact', 'Inquiries'],
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'py-3 bg-[#FDFCF7]/90 backdrop-blur-md border-b border-black/[0.06] shadow-sm' : 'py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between">
        {/* Brand */}
        <div data-testid="nav-brand" className="flex items-center gap-3">
          <span className="font-serif text-xl tracking-tight text-[#121212] font-light">Elena Vance</span>
          <span className="hidden sm:flex items-center gap-1.5 text-[10px] font-mono text-neutral-500 border border-neutral-200 rounded-full px-2.5 py-1 uppercase tracking-widest">
            <span className="w-1.5 h-1.5 rounded-full bg-[#B88349] animate-pulse" />
            Available Q3/Q4
          </span>
        </div>

        {/* Desktop Links */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map(([id, label]) => (
            <button
              key={id}
              data-testid={`nav-link-${id}`}
              onClick={() => scrollTo(id)}
              className="text-[11px] uppercase font-mono tracking-[0.22em] text-neutral-500 hover:text-[#121212] transition-colors duration-300"
            >
              {label}
            </button>
          ))}
        </nav>

        {/* CTA */}
        <button
          data-testid="nav-cta-contact"
          onClick={() => scrollTo('contact')}
          className="hidden md:block bg-[#121212] text-white rounded-full px-6 py-2.5 text-[11px] uppercase font-mono tracking-widest hover:bg-neutral-700 active:scale-[0.98] transition-all duration-300"
        >
          Start a Project
        </button>

        {/* Mobile Hamburger */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="md:hidden flex flex-col gap-1.5 p-2"
          aria-label="Toggle menu"
        >
          <motion.span
            animate={{ rotate: menuOpen ? 45 : 0, y: menuOpen ? 8 : 0 }}
            className="block w-5 h-px bg-[#121212]"
          />
          <motion.span
            animate={{ opacity: menuOpen ? 0 : 1 }}
            className="block w-5 h-px bg-[#121212]"
          />
          <motion.span
            animate={{ rotate: menuOpen ? -45 : 0, y: menuOpen ? -8 : 0 }}
            className="block w-5 h-px bg-[#121212]"
          />
        </button>
      </div>

      {/* Mobile Menu */}
      <motion.div
        initial={false}
        animate={{ height: menuOpen ? 'auto' : 0, opacity: menuOpen ? 1 : 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="md:hidden overflow-hidden border-t border-black/[0.06]"
      >
        <div className="px-6 py-6 flex flex-col gap-5 bg-[#FDFCF7]">
          {navLinks.map(([id, label]) => (
            <button
              key={id}
              onClick={() => scrollTo(id)}
              className="text-left text-xs font-mono uppercase tracking-[0.22em] text-neutral-600 hover:text-[#121212] transition-colors"
            >
              {label}
            </button>
          ))}
          <button
            onClick={() => scrollTo('contact')}
            className="w-full bg-[#121212] text-white rounded-full px-6 py-3 text-xs uppercase font-mono tracking-widest"
          >
            Start a Project
          </button>
        </div>
      </motion.div>
    </header>
  );
}
